/**
 * Collection Statistics Page - Detailed collection metrics with charts.
 * Shows inventory, growth, usage patterns, and collection health.
 */

import { useQuery } from "@tanstack/react-query";
import { get } from "@/api/client";
import {
  ArrowLeft,
  Clock,
  BookOpen,
  Library,
  Package,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Globe,
  MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";
import { DonutChart, SimpleBarChart, TrendChart, StatTile, ProgressBar } from "../components";

interface CollectionStatsData {
  totalBiblioRecords: number;
  totalItems: number;
  availableItems: number;
  onLoanItems: number;
  damagedItems: number;
  lostItems: number;
  totalAuthorities: number;
  recordsAddedThisMonth: number;
  recordsAddedThisYear: number;
  itemsAddedThisMonth: number;
  collectionGrowthRate: number;
  averageItemsPerRecord: number;
  recordsByType: Record<string, number>;
  recordsByLanguage: Record<string, number>;
  itemsByStatus: Record<string, number>;
  itemsByLocation: Record<string, number>;
  newRecordsByMonth: Record<string, number>;
  topCirculatedTitles: Record<string, number>;
  turnoverByType: Record<string, number>;
  generatedAt: string;
}

const typeLabels: Record<string, string> = {
  book: "Livres",
  periodical: "Périodiques",
  dvd: "DVD",
  cd: "CD Audio",
  ebook: "E-books",
  audiobook: "Livres audio",
  other: "Autres",
};

const languageLabels: Record<string, string> = {
  fre: "Français",
  eng: "Anglais",
  ger: "Allemand",
  spa: "Espagnol",
  ita: "Italien",
  por: "Portugais",
  ara: "Arabe",
  und: "Non défini",
};

const typeColors: Record<string, string> = {
  book: "#3b82f6",
  periodical: "#8b5cf6",
  dvd: "#f59e0b",
  cd: "#22c55e",
  ebook: "#06b6d4",
  audiobook: "#ec4899",
  other: "#6b7280",
};

export function CollectionStatsPage() {
  const { data, isLoading, error } = useQuery<CollectionStatsData>({
    queryKey: ["statistics", "collection"],
    queryFn: () => get<CollectionStatsData>("/statistics/collection"),
    refetchInterval: 120000,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <p className="mt-4 font-medium text-red-800 dark:text-red-200">
            Erreur lors du chargement des statistiques
          </p>
        </div>
      </div>
    );
  }

  const availabilityRate = data.totalItems > 0 ? (data.availableItems / data.totalItems) * 100 : 0;

  // Prepare chart data
  const itemStatusData = [
    { name: "Disponibles", value: data.availableItems, color: "#22c55e" },
    { name: "En prêt", value: data.onLoanItems, color: "#3b82f6" },
    { name: "Endommagés", value: data.damagedItems, color: "#f59e0b" },
    { name: "Perdus", value: data.lostItems, color: "#ef4444" },
  ].filter((item) => item.value > 0);

  const recordsByTypeData = Object.entries(data.recordsByType)
    .map(([type, count]) => ({
      name: typeLabels[type] || type,
      value: count,
      color: typeColors[type] || "#6b7280",
    }))
    .sort((a, b) => b.value - a.value);

  const recordsByLanguageData = Object.entries(data.recordsByLanguage)
    .map(([lang, count]) => ({
      name: languageLabels[lang] || lang,
      value: count,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  const itemsByLocationData = Object.entries(data.itemsByLocation)
    .map(([location, count]) => ({
      name: location,
      value: count,
    }))
    .sort((a, b) => b.value - a.value);

  // Monthly growth trend
  const monthlyGrowthData = Object.entries(data.newRecordsByMonth)
    .map(([month, count]) => ({
      name: month.substring(5), // Extract MM from YYYY-MM
      value: count,
    }))
    .slice(-12);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/statistics"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Statistiques des collections
            </h1>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              Analyse détaillée du fonds documentaire
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <Clock className="h-4 w-4" />
          {new Date(data.generatedAt).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Notices"
          value={data.totalBiblioRecords}
          change={
            data.recordsAddedThisMonth > 0
              ? Math.round((data.recordsAddedThisMonth / data.totalBiblioRecords) * 100 * 10) / 10
              : 0
          }
          changeLabel="ce mois"
          icon={<BookOpen className="h-5 w-5" />}
          color="blue"
          sparklineData={[85, 92, 88, 95, 102, 98, 108, 112, 118, 125]}
        />
        <StatTile
          label="Exemplaires"
          value={data.totalItems}
          icon={<Library className="h-5 w-5" />}
          color="purple"
          sparklineData={[220, 228, 235, 242, 238, 255, 262, 270, 278, 285]}
        />
        <StatTile
          label="Disponibles"
          value={data.availableItems}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="green"
        />
        <StatTile
          label="Autorités"
          value={data.totalAuthorities}
          icon={<Package className="h-5 w-5" />}
          color="gray"
        />
      </div>

      {/* Charts Row - Item Status & Types */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Item Status Donut */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            État des exemplaires
          </h2>
          <div className="flex flex-col items-center">
            <DonutChart
              data={itemStatusData}
              centerValue={data.totalItems}
              centerLabel="exemplaires"
              size={220}
            />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Disponibles</span>
              </div>
              <span className="font-bold text-green-600 dark:text-green-400">
                {availabilityRate.toFixed(0)}%
              </span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">En prêt</span>
              </div>
              <span className="font-bold text-blue-600 dark:text-blue-400">
                {data.onLoanItems.toLocaleString("fr-FR")}
              </span>
            </div>
          </div>
        </div>

        {/* Records by Type */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Répartition par type
          </h2>
          {recordsByTypeData.length > 0 ? (
            <DonutChart
              data={recordsByTypeData}
              centerValue={data.totalBiblioRecords}
              centerLabel="notices"
              size={220}
            />
          ) : (
            <div className="flex h-60 items-center justify-center text-gray-500 dark:text-gray-400">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Growth Section */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Croissance du fonds
            </h2>
          </div>
          <div
            className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium ${
              data.collectionGrowthRate > 0
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : data.collectionGrowthRate < 0
                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {data.collectionGrowthRate > 0 ? (
              <TrendingUp className="h-4 w-4" />
            ) : data.collectionGrowthRate < 0 ? (
              <TrendingDown className="h-4 w-4" />
            ) : null}
            {data.collectionGrowthRate > 0 ? "+" : ""}
            {data.collectionGrowthRate.toFixed(1)}% cette année
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {monthlyGrowthData.length > 0 ? (
              <TrendChart data={monthlyGrowthData} height={250} color="#22c55e" />
            ) : (
              <div className="flex h-60 items-center justify-center text-gray-500 dark:text-gray-400">
                Aucune donnée de croissance disponible
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                +{data.recordsAddedThisMonth}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Notices ce mois</div>
            </div>
            <div className="rounded-lg bg-purple-50 p-4 dark:bg-purple-900/20">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                +{data.recordsAddedThisYear}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Notices cette année</div>
            </div>
            <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                +{data.itemsAddedThisMonth}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Exemplaires ce mois</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700/50">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.averageItemsPerRecord.toFixed(2)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Exemplaires/notice</div>
            </div>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* By Language */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Répartition par langue
            </h2>
          </div>
          {recordsByLanguageData.length > 0 ? (
            <SimpleBarChart data={recordsByLanguageData} height={280} horizontal />
          ) : (
            <div className="flex h-60 items-center justify-center text-gray-500 dark:text-gray-400">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* By Location */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Répartition par établissement
            </h2>
          </div>
          {itemsByLocationData.length > 0 ? (
            <SimpleBarChart data={itemsByLocationData} height={280} horizontal />
          ) : (
            <div className="flex h-60 items-center justify-center text-gray-500 dark:text-gray-400">
              Aucune donnée disponible
            </div>
          )}
        </div>
      </div>

      {/* Health Indicators */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">
          Santé de la collection
        </h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Taux de disponibilité
              </span>
              <span className="text-sm font-bold text-green-600 dark:text-green-400">
                {availabilityRate.toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={availabilityRate}
              max={100}
              color="#22c55e"
              showLabel={false}
              size="lg"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                En circulation
              </span>
              <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                {((data.onLoanItems / data.totalItems) * 100).toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={(data.onLoanItems / data.totalItems) * 100}
              max={100}
              color="#3b82f6"
              showLabel={false}
              size="lg"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Endommagés
              </span>
              <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                {((data.damagedItems / data.totalItems) * 100).toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={(data.damagedItems / data.totalItems) * 100}
              max={100}
              color="#f59e0b"
              showLabel={false}
              size="lg"
              thresholds={{ warning: 3, danger: 5 }}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Perdus</span>
              <span className="text-sm font-bold text-red-600 dark:text-red-400">
                {((data.lostItems / data.totalItems) * 100).toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={(data.lostItems / data.totalItems) * 100}
              max={100}
              color="#ef4444"
              showLabel={false}
              size="lg"
              thresholds={{ warning: 1, danger: 2 }}
            />
          </div>
        </div>

        {/* Status summary cards */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Disponibles
              </span>
            </div>
            <div className="mt-2 text-2xl font-bold text-green-600 dark:text-green-400">
              {data.availableItems.toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">En prêt</span>
            </div>
            <div className="mt-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
              {data.onLoanItems.toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Endommagés
              </span>
            </div>
            <div className="mt-2 text-2xl font-bold text-amber-600 dark:text-amber-400">
              {data.damagedItems.toLocaleString("fr-FR")}
            </div>
          </div>
          <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Perdus</span>
            </div>
            <div className="mt-2 text-2xl font-bold text-red-600 dark:text-red-400">
              {data.lostItems.toLocaleString("fr-FR")}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to="/catalog"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <BookOpen className="h-4 w-4" />
          Voir le catalogue
        </Link>
        <Link
          to="/ai/weeding"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <XCircle className="h-4 w-4" />
          Suggestions de désherbage
        </Link>
        <Link
          to="/statistics"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <TrendingUp className="h-4 w-4" />
          Vue d'ensemble
        </Link>
      </div>
    </div>
  );
}
